import { round } from "@/lib/utils";
import { AnimatedImageDto } from "@repo/api/animated-image";
import { CreativeProjectTimelineItem } from "@repo/api/creative-project";
import { CompositionDto } from "@repo/api/editor";
import {
  CompositionTimings,
  getCompositionTimings,
  getMaxDurationMs,
  msToFrames,
} from "@repo/video-engine-i2v/utils/timing";

export type BeatSyncErrorType =
  | "NOT_ANIMATED"
  | "ANIMATION_NOT_COMPLETED"
  | "CLIP_TOO_SHORT"
  | "FAILED";

export const BEAT_SYNC_ERROR_MESSAGES = {
  NOT_ANIMATED: "You have to animate all images before applying beat sync",
  ANIMATION_NOT_COMPLETED:
    "Wait for the animations to complete before applying beat sync",
  CLIP_TOO_SHORT: "Some clips are too short to fit the beat sync",
  FAILED: "Failed to sync clips to beat",
};

export class BeatSyncError extends Error {
  type: BeatSyncErrorType;

  constructor(type: BeatSyncErrorType) {
    super(type);
    this.name = "BeatSyncError";
    this.type = type;
  }
}

type TimelineItemWithAnimatedImage = {
  timelineItem: CreativeProjectTimelineItem;
  animatedImage: AnimatedImageDto;
};

function mapTimelineItemsWithAnimatedImages({
  composition,
  animatedImages,
}: {
  composition: CompositionDto;
  animatedImages: Record<string, AnimatedImageDto>;
}): TimelineItemWithAnimatedImage[] {
  return composition.mainSceneTimeline.map((timelineItem) => {
    if (!timelineItem.animatedImageExternalId) {
      throw new BeatSyncError("NOT_ANIMATED");
    }

    const animatedImage = animatedImages[timelineItem.animatedImageExternalId];

    if (!animatedImage) {
      throw new BeatSyncError("NOT_ANIMATED");
    }

    if (animatedImage.status !== "COMPLETED") {
      throw new BeatSyncError("ANIMATION_NOT_COMPLETED");
    }

    return {
      timelineItem,
      animatedImage,
    };
  });
}

function framesToDurationSeconds(
  frames: number,
  fps: number,
  maxDurationMs: number,
): number {
  // Adds 0.5 frames to ensure that rounding in timing.ts rounds to the correct frame.
  let durationMs = Math.floor(((frames + 0.5) * 1000) / fps);

  if (durationMs > maxDurationMs) {
    durationMs = maxDurationMs;
  }

  return round(durationMs / 1000, 3);
}

function adjustCutPoints({
  composition,
  compositionTimings,
  cutPointsMs,
}: {
  composition: CompositionDto;
  compositionTimings: CompositionTimings;
  cutPointsMs: number[];
}): number[] {
  const fps = composition.fps;
  const bgMusicStartMs = composition.backgroundMusic?.startMs ?? 0;
  const bgMusicStartSilenceMs =
    composition.backgroundMusic?.startSilenceMs ?? 0;
  const startSceneGlobalEndAtFrames =
    compositionTimings.startScene?.durationInFrames ?? 0;

  // The music offsets have to be converted the same way BackgroundMusic.tsx
  // converts them, or beat sync's idea of where the music sits differs from
  // where it actually sits. The cut point itself has no renderer counterpart,
  // so it goes to the nearest frame.
  const bgMusicStartFrames = msToFrames(bgMusicStartMs, fps);
  const bgMusicStartSilenceFrames = msToFrames(bgMusicStartSilenceMs, fps);

  let cutPointFrames = cutPointsMs.map(
    (cutPointMs) =>
      Math.round((cutPointMs * fps) / 1000) -
      bgMusicStartFrames +
      bgMusicStartSilenceFrames,
  );

  if (
    !bgMusicStartMs &&
    !bgMusicStartSilenceMs &&
    !startSceneGlobalEndAtFrames
  ) {
    return cutPointFrames;
  }

  const minCutPointFrame = Math.max(
    bgMusicStartSilenceFrames,
    startSceneGlobalEndAtFrames,
  );

  // Excludes all cut points that are before the first clip starts.
  cutPointFrames = cutPointFrames.filter(
    (cutPointFrame) => cutPointFrame > minCutPointFrame,
  );

  if (cutPointFrames.length === 0) {
    return [];
  }

  const firstClipFrames = cutPointFrames[0]! - startSceneGlobalEndAtFrames;

  if (firstClipFrames < msToFrames(1500, fps)) {
    // To prevent the first clip from just flashing, we skip the first cut point.
    cutPointFrames = cutPointFrames.slice(1);
  }

  return cutPointFrames;
}

function splitCutPoints({
  index,
  cutPointFrames,
  maxDurationFrames,
  startSceneGlobalEndAtFrames,
  fps,
}: {
  index: number;
  cutPointFrames: number[];
  maxDurationFrames: number;
  startSceneGlobalEndAtFrames: number;
  fps: number;
}): {
  cutPointFrames: number[];
  requiredDurationFrames: number;
} {
  const cutPointFrame = cutPointFrames[index]!;

  const previousCutPointFrame =
    index > 0 ? cutPointFrames[index - 1]! : startSceneGlobalEndAtFrames;
  const durationFrames = cutPointFrame - previousCutPointFrame;

  if (durationFrames < maxDurationFrames) {
    return {
      cutPointFrames,
      requiredDurationFrames: durationFrames,
    };
  }

  const middleCutPointFrame = Math.round(
    (previousCutPointFrame + cutPointFrame) / 2,
  );

  if (middleCutPointFrame - previousCutPointFrame <= msToFrames(1000, fps)) {
    throw new BeatSyncError("CLIP_TOO_SHORT");
  }

  return splitCutPoints({
    index,
    cutPointFrames: [
      ...cutPointFrames.slice(0, index),
      middleCutPointFrame,
      ...cutPointFrames.slice(index),
    ],
    maxDurationFrames,
    startSceneGlobalEndAtFrames,
    fps,
  });
}

function syncTimelineItemToBeat({
  composition,
  compositionTimings,
  index,
  cutPointFrames,
  timelineItemsWithAnimatedImages,
}: {
  composition: CompositionDto;
  compositionTimings: CompositionTimings;
  index: number;
  cutPointFrames: number[];
  timelineItemsWithAnimatedImages: TimelineItemWithAnimatedImage[];
}): {
  cutPointFrames: number[];
} {
  const currentTimelineItem = timelineItemsWithAnimatedImages[index];
  const currentConfig =
    currentTimelineItem?.timelineItem.animatedImageConfigs[
      currentTimelineItem.animatedImage.externalId
    ];

  if (!currentTimelineItem || !currentConfig) {
    console.error(`No timeline item or config found for index ${index}`);
    throw new BeatSyncError("FAILED");
  }

  const fps = composition.fps;

  let adjustedCutPointFrames = cutPointFrames;

  const cutPointFrame = adjustedCutPointFrames[index];

  if (cutPointFrame === undefined) {
    return {
      cutPointFrames,
    };
  }

  const startSceneGlobalEndAtFrames =
    compositionTimings.startScene?.durationInFrames ?? 0;

  const startSceneExitTransitionFrames =
    compositionTimings.startScene?.exitTransitionFrames ?? 0;

  const previousCutPointFrame =
    index > 0
      ? adjustedCutPointFrames[index - 1]!
      : startSceneGlobalEndAtFrames;

  const itemTiming =
    compositionTimings.mainScene.compositionImageTimings[index];

  if (!itemTiming) {
    console.error(`No timing found for item ${index}`);
    throw new BeatSyncError("FAILED");
  }

  const cutDurationFrames = cutPointFrame - previousCutPointFrame;

  const maxDurationFrames = Math.min(
    msToFrames(4000, fps),
    msToFrames(
      getMaxDurationMs(currentTimelineItem.animatedImage, currentConfig),
      fps,
    ),
  );
  // The composition timings are calculated before the loop, so a transition that
  // an earlier iteration removed would otherwise still be counted here.
  const entryTransitionFrames = currentTimelineItem.timelineItem.entryTransition
    ? itemTiming.entryTransitionFrames
    : 0;
  const exitTransitionFrames = itemTiming.exitTransitionFrames;

  let requiredDurationFrames = cutDurationFrames;

  if (index === 0) {
    requiredDurationFrames += startSceneExitTransitionFrames;
  } else {
    requiredDurationFrames += entryTransitionFrames;

    if (entryTransitionFrames && requiredDurationFrames > maxDurationFrames) {
      // Clip won't reach the cut point with entry transition, remove it.
      delete currentTimelineItem.timelineItem.entryTransition;
      requiredDurationFrames = cutDurationFrames;
    }
  }

  // Clip won't reach the cut point, split the cut points recursively.
  if (requiredDurationFrames > maxDurationFrames) {
    const result = splitCutPoints({
      index,
      cutPointFrames: adjustedCutPointFrames,
      maxDurationFrames:
        index === 0
          ? // Hack so that we can use the same splitting logic for the first clip.
            Math.max(0, maxDurationFrames - startSceneExitTransitionFrames)
          : maxDurationFrames,
      startSceneGlobalEndAtFrames,
      fps,
    });

    adjustedCutPointFrames = result.cutPointFrames;
    requiredDurationFrames =
      index === 0
        ? result.requiredDurationFrames + startSceneExitTransitionFrames
        : result.requiredDurationFrames;
  }

  currentConfig.duration = framesToDurationSeconds(
    requiredDurationFrames,
    fps,
    getMaxDurationMs(currentTimelineItem.animatedImage, currentConfig),
  );

  const visibleTimeFrames =
    adjustedCutPointFrames[index]! -
    previousCutPointFrame -
    exitTransitionFrames;

  if (visibleTimeFrames < msToFrames(1500, fps)) {
    const nextTimelineItem = timelineItemsWithAnimatedImages[index + 1];
    delete nextTimelineItem?.timelineItem.entryTransition;
  }

  return {
    cutPointFrames: adjustedCutPointFrames,
  };
}

export function syncMainSceneTimelineToBeat({
  cutPointsMs,
  composition,
  animatedImages,
}: {
  cutPointsMs: number[];
  composition: CompositionDto;
  animatedImages: Record<string, AnimatedImageDto>;
}) {
  const compositionTimings = getCompositionTimings(composition, animatedImages);

  const timelineItemsWithAnimatedImages = mapTimelineItemsWithAnimatedImages({
    composition,
    animatedImages,
  });

  let cutPointFrames = adjustCutPoints({
    composition,
    compositionTimings,
    cutPointsMs,
  });

  for (let i = 0; i < timelineItemsWithAnimatedImages.length; i++) {
    const result = syncTimelineItemToBeat({
      composition,
      compositionTimings,
      index: i,
      cutPointFrames,
      timelineItemsWithAnimatedImages,
    });

    cutPointFrames = result.cutPointFrames;
  }

  return timelineItemsWithAnimatedImages.map(
    ({ timelineItem }) => timelineItem,
  );
}
